import { Box, Container, Flex, Heading, StackItem, useDisclosure, VStack } from '@chakra-ui/react';
import { nodeParser } from '@kontent-ai/delivery-node-parser';
import { createRichTextHtmlResolver } from '@kontent-ai/delivery-sdk';
import { getButtonWithLinkStyling } from 'components/base/Button';
import { Link as CustomLink } from 'components/base/Link';
import { HeadComponentContact } from 'components/pages/Head';
import { emptyBody } from 'helpers/htmlResolver';
import SchemaMarkup from 'helpers/schemaMarkup';
import { LinkType } from 'lib/enums/LinkType';
import { Contact, Contact as ContactModel, FormModal } from 'models';
import { useRouter } from 'next/router';
import { withTranslations } from 'store/translations';
import { ContentType } from 'utils/componentMapper';

import { EditorialSpots as EditorialSpotsModel } from '../../../models';
import { Link } from '../../base/Link';
import { ContactFormModal } from '../../modules/ContactFormModal';
import { EditorialSpots } from '../../modules/EditorialSpots';
import Person from './Person';
type ContactUsPersonProps = {
  person: Omit<ContactModel['elements'], 'key'>;
  form: FormModal | null;
  contact: Contact;
};

const ContactUsPerson = ({ person, form, contact }: ContactUsPersonProps) => {
  const { common, multiple, url, aria } = withTranslations();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const router = useRouter();
  const personMarkup = person?.jsonLdMarkupPerson?.value;
  const data = {
    keyCompetenciesHtml: createRichTextHtmlResolver(nodeParser).resolveRichText({
      element: person.keyCompetencies,
      urlResolver: (linkId, linkText, link) => {
        return {
          linkHtml: `<a class="xLink" href="/${link?.urlSlug}">${linkText}</a>`,
        };
      },
    }).html,
    recentFocusHtml: createRichTextHtmlResolver(nodeParser).resolveRichText({
      element: person.recentFocus,
      urlResolver: (linkId, linkText, link) => {
        return {
          linkHtml: `<a class="xLink" href="/${link?.urlSlug}">${linkText}</a>`,
        };
      },
    }).html,
    credentialsHtml: createRichTextHtmlResolver(nodeParser).resolveRichText({
      element: person.credentials,
      urlResolver: (linkId, linkText, link) => {
        return {
          linkHtml: `<a class="xLink" href="/${link?.urlSlug}">${linkText}</a>`,
        };
      },
    }).html,
    specialCompetenciesHtml: createRichTextHtmlResolver(nodeParser).resolveRichText({
      element: person.specialCompetencies,
      urlResolver: (linkId, linkText, link) => {
        return {
          linkHtml: `<a class="xLink" href="/${link?.urlSlug}">${linkText}</a>`,
        };
      },
    }).html,
  };
  // console.log('person', person);

  const noOtherValues =
    person.keyCompetencies.value === emptyBody &&
    person.recentFocus.value === emptyBody &&
    person.credentials.value === emptyBody &&
    person.specialCompetencies.value === emptyBody;

  return (
    <>
      <SchemaMarkup id='person' jsonLd={personMarkup} />
      <HeadComponentContact page={contact}></HeadComponentContact>
      <Box bg='pebble.20' py='m'>
        <Container>
          <CustomLink
            shallow
            colorScheme='mountain.base'
            href={`/${multiple('contactUsUrl')}/${url('peopleUrl')}`}
            iconName='chevronLeft'
            type={LinkType.SecondaryButton}
            locale={router.locale}
          >
            {common('contactUs')}
          </CustomLink>
          <Heading
            as='h1'
            color='mountain.base'
            flex={{ base: 'unset', lg: '1' }}
            mt='s'
            textStyle={{ base: 'mobile.h3', lg: 'desktop.h4' }}
          >
            {person.displayName.value}
            {/* {common('contactUs')} */}
          </Heading>
        </Container>
      </Box>
      <Container pt='l' pb='s'>
        <Flex gap='20%' flexDirection={{ base: 'column', md: 'row' }}>
          <VStack mb={'xs'}>
            <Person hasContact={false} {...person} showMarketInsteadOfName />
            {form && (
              <Box w='100%' textAlign={{ base: 'center', md: 'left' }}>
                {getButtonWithLinkStyling(
                  `${aria('openContactGetInTouchWith')} ${person.displayName.value}`,
                  common('getInTouch'),
                  onOpen,
                  form.system.name,
                )}
              </Box>
            )}
          </VStack>

          {person.description.value != emptyBody && (
            <Box
              alignContent={'center'}
              color='black'
              mb='s'
              textStyle='mobile.body.l'
              dangerouslySetInnerHTML={{ __html: person.description.value }}
            />
          )}
          {!noOtherValues && (
            <Box flexDirection='column'>
              {person.keyCompetencies.value != emptyBody && (
                <Box mb='s'>
                  {person.keyCompetencies.name && (
                    <Box as='h2' color='black' mb='xs' textStyle='desktop.body.medium.l'>
                      {common('keyCompetencies')}
                    </Box>
                  )}
                  <Box
                    color='black'
                    textStyle='desktop.body.l'
                    dangerouslySetInnerHTML={{ __html: data.keyCompetenciesHtml }}
                    sx={{
                      p: {
                        marginBottom: '1em',
                      },
                      a: {
                        color: 'cyan.ramboll',
                        textDecor: 'underline',
                      },
                      'ul, ol': {
                        listStylePosition: 'inside',
                      },
                    }}
                  />
                </Box>
              )}

              {person.recentFocus.value != emptyBody && (
                <Box mb='s'>
                  {person.recentFocus.name && (
                    <Box as='h2' color='black' mb='xs' textStyle='desktop.body.medium.l'>
                      {common('recentFocus')}
                    </Box>
                  )}
                  <Box
                    color='black'
                    sx={{
                      p: {
                        marginBottom: '1em',
                      },
                      a: {
                        color: 'cyan.ramboll',
                        textDecor: 'underline',
                      },
                      'ul, ol': {
                        listStylePosition: 'inside',
                      },
                    }}
                    textStyle='desktop.body.l'
                    dangerouslySetInnerHTML={{ __html: data.recentFocusHtml }}
                  />
                </Box>
              )}
              {person.credentials.value != emptyBody && (
                <Box mb='s'>
                  {person.credentials.name && (
                    <Box as='h2' color='black' mb='xs' textStyle='desktop.body.medium.l'>
                      {common('credentials')}
                    </Box>
                  )}
                  <Box
                    color='black'
                    textStyle='desktop.body.l'
                    sx={{
                      p: {
                        marginBottom: '1em',
                      },
                      a: {
                        color: 'cyan.ramboll',
                        textDecor: 'underline',
                      },
                      'ul, ol': {
                        listStylePosition: 'inside',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: data.credentialsHtml }}
                  />
                </Box>
              )}
              {person.specialCompetencies.value != emptyBody && (
                <Box mb='s'>
                  {person.specialCompetencies.name && (
                    <Box as='h2' color='black' mb='xs' textStyle='desktop.body.medium.l'>
                      {common('specialCompetencies')}
                    </Box>
                  )}
                  <Box
                    color='black'
                    textStyle='desktop.body.l'
                    sx={{
                      p: {
                        marginBottom: '1em',
                      },
                      a: {
                        color: 'cyan.ramboll',
                        textDecor: 'underline',
                      },
                      'ul, ol': {
                        listStylePosition: 'inside',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: data.specialCompetenciesHtml }}
                  />
                </Box>
              )}
              {person.ctaSnippetUrl?.value && (
                <Link
                  href={person.ctaSnippetUrl.value}
                  iconName='chevronRightForLink'
                  type={LinkType.SecondaryButton}
                  isExternal={person.ctaSnippetOpenIn.value[0]?.codename === 'new_tab'}
                  locale={router.locale}
                >
                  {person.ctaSnippetCtaText.value}
                </Link>
              )}
            </Box>
          )}
          {form && (
            <ContactFormModal
              id={`${person.id}${form.system.id}`}
              model={form}
              isOpen={isOpen}
              contactPersonEmail={person.email.value}
              onClose={onClose}
            />
          )}
        </Flex>
        <Box pt={'5%'}>
          <VStack shouldWrapChildren spacing={0}>
            {person?.relatedItems.linkedItems.map((element, index) => {
              const type = element.system.type as ContentType;
              if (type === ContentType.editorial_spots) {
                return (
                  <StackItem key={index} display='block'>
                    <EditorialSpots model={element as EditorialSpotsModel} />
                  </StackItem>
                );
              }
              return null;
            })}
          </VStack>
        </Box>
      </Container>
    </>
  );
};

export default ContactUsPerson;
